import Analytics, { AnalyticsInstance } from 'analytics';
import { GtmData, SessionsTypes, UserData } from '../apis/user/types';
import googleTagManager from '@analytics/google-tag-manager';
import customerIo from '@analytics/customerio';
import intercomPlugin from '@analytics/intercom';
import { getUnixTime } from 'date-fns';
import googleAnalytics from '@analytics/google-analytics';
import { track, trackClick, init as initTracker } from '@zignaly-open/tracker';
import { isFeatureOn, isZignaly, whitelabel } from '../whitelabel';
import { Features } from '@zignaly-open/ps2-definitions';
import { Capacitor } from '@capacitor/core';

initTracker({
  baseApi: whitelabel.baseApi,
  platform: Capacitor.getPlatform(),
});

let analytics: AnalyticsInstance | null = null;
const enableTracking = isFeatureOn(Features.EnableTracking);
const trackingQueue: Array<() => void> = [];

// Initialize analytics after page load
const initializeAnalytics = () => {
  const customerIoPlugin = customerIo({
    siteId: import.meta.env.VITE_CUSTOMER_IO_SITE_ID,
  });
  const originalInitialize = customerIoPlugin.initialize;

  customerIoPlugin.initialize = (args: unknown) => {
    originalInitialize.call(customerIoPlugin, args);
    const cio = document.getElementById('cio-tracker');
    cio?.setAttribute('data-use-in-app', 'true');
  };

  analytics = Analytics({
    app: 'zignaly',
    plugins: [
      whitelabel.tools?.google_tag_manager &&
        googleTagManager({
          containerId: whitelabel.tools?.google_tag_manager,
        }),
      googleAnalytics({
        measurementIds: [import.meta.env.VITE_GA_ID],
      }),
      customerIoPlugin,
      whitelabel.tools?.intercom &&
        intercomPlugin({
          appId: whitelabel.tools?.intercom,
        }),
    ].filter(Boolean),
  });

  runGtagFnWithMeasurementId('config', undefined);
  // Process queued events
  trackingQueue.forEach((fn) => fn());
};

// Defer analytics initialization
if (typeof jest === 'undefined' && enableTracking) {
  window.addEventListener('load', () => {
    setTimeout(initializeAnalytics, 3000);
  });
}

export const trackWithQueue = (trackFn: () => void) => {
  if (analytics) {
    trackFn();
  } else if (enableTracking) {
    trackingQueue.push(trackFn);
  }
};

export const trackNewSession = (
  userData: UserData,
  eventType: SessionsTypes,
) => {
  trackWithQueue(() => {
    const { email, userId, firstName, createdAt } = userData;

    runGtagFnWithMeasurementId('config', {
      user_id: userId,
    });

    try {
      analytics?.identify(userId, {
        email,
        name: firstName,
        created_at: getUnixTime(new Date(createdAt)),
      });

      if (eventType === SessionsTypes.Signup) {
        analytics?.track('newUser', { userId });
        twq(userId).trackVerify();
      } else {
        gtagEvent('login_completed', { email, userId });
      }
    } catch (e) {
      console.error(e);
    }
  });
};

export const trackEndSession = () => {
  runGtagFnWithMeasurementId('config', {
    user_id: null,
  });
};

export const trackPage = () => {
  trackWithQueue(() => analytics?.page());
};

export const gtagEvent = (
  eventName: string,
  payload?: Record<string, unknown>,
) => {
  try {
    analytics?.track(eventName, payload || {}, {
      plugins: {
        all: false,
        'google-analytics': true,
      },
    });
  } catch (e) {
    console.error(e);
  }
};

export const trackAllocation = (
  userId: string,
  amount: string,
  coin: string,
  serviceId: string,
) => {
  trackWithQueue(() => {
    gtagEvent('allocation_created', {
      value: amount,
      currency: coin,
      serviceId,
    });
    twq(userId).trackAllocation();
  });
};

export const twq = (userId: string) => {
  const twqWrapper = (eventKey: string, eventData: object = {}) => {
    if (enableTracking) {
      window.twq?.('event', eventKey, {
        contents: [{ content_id: userId }],
        ...eventData,
      });
    }
  };

  const trackVerify = () => {
    twqWrapper('tw-og0cu-og0hv');
  };

  const trackAllocationFn = () => {
    twqWrapper('tw-og0cu-og0hz');
  };

  return { trackVerify, trackAllocation: trackAllocationFn };
};

export const tzTrack: typeof track = (...args) =>
  isZignaly && enableTracking && track(...args);

export const tzTrackClick: typeof trackClick = (...args) =>
  isZignaly && enableTracking && trackClick(...args);

export const getGtmClientIdSessionId: () => Promise<GtmData | null> =
  async () => {
    try {
      const measurementId = whitelabel.tools?.ga_measurement_id;
      if (!whitelabel.tools?.google_tag_manager || !measurementId) {
        return null;
      }

      // cookies seem to be the most reliable way
      // because we do not have metric ids and
      // we also do not have the analytics object ready yet
      const gtagClientId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('_ga='))
        ?.match(/GA\d+\.\d+\.(\d+\.\d+)/)?.[1];

      const gtagSessionIds = document.cookie
        .split('; ')
        .filter(
          (row) =>
            row.startsWith(`_ga_`) && row.includes(measurementId.slice(2)),
        )
        .map((x) => x.match(/(?:GS1\.1\.|s)(\d+)/)?.[1]);

      return {
        ga_session_id: gtagClientId,
        ga_client_id: gtagSessionIds[0],
      };
    } catch (e) {
      console.error(e);
      return null;
    }
  };

const runGtagFnWithMeasurementId = (name: string) => {
  try {
    const measurementId = whitelabel.tools?.ga_measurement_id;
    // the subsequent identify does not run the 'config' event, it only does 'set'
    window.gtag?.(name, measurementId, {
      user_id: null,
    });
  } catch (e) {
    console.error(e);
  }
};
